import React, { useState, useEffect, memo, useRef } from 'react';
import { Trans, t } from '@lingui/macro';
import { CSSTransition } from 'react-transition-group';
import { ChevronRight, ChevronUp, Loader2, CornerDownRight } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar';
import { UserAvatar } from '../components/UserAvatar';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { UtilApp } from '../../services/Utils/UtilApp';
import { Util } from '../../services/Utils/Util';
import { My } from '../../services/My';
import { getDisplayName } from '../../services/Utils/userDisplayName';
import { Fire } from '../../services/FireClient';
import { MyNotifs } from '../../services/MyNotifs';
import { Message, SendFile } from '../../models/Message';
import { Room } from '../../models/Room';
import { RoomToken } from '../../models/RoomToken';
import { UIMessage } from '../components/UIMessage';
import MessageItem, { MessageItemState, GetReactionSummary } from '../components/MessageItem';
import MsgInputFooterReactions from '../components/message-inputs/MsgInputFooterReactions';
import { Badge } from '../../components/ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { IconChevronDown, IconSmilePlus } from '../components/FigmaIcons';

interface RoomMessageItemProps {
    message: UIMessage;
    room: Room;
    activeThreads: Set<string>;
    toggleActiveThread: (id: string) => void;
    highlightedId: string | null;
    highlightedReaction?: { messageId: string; emoticon: string } | null;
    currentMessage: UIMessage | null;
    messageRefs: React.MutableRefObject<{ [key: string]: HTMLDivElement | null }>;
    getNodeRef: (id: string) => React.RefObject<HTMLDivElement>;
    getMessageItemState: (m: UIMessage) => MessageItemState;
    onMessageClick: (m: UIMessage) => void;
    progressForFile: { [key: string]: SendFile };
    onResendMessage: (m: UIMessage) => void;
    mentionTarget: { user: any, messageId: string, threadId: string } | null;
    setMentionTarget: (target: { user: any, messageId: string, threadId: string } | null) => void;
    forceUpdate: () => void;
    onTargetMessage: (messageId: string | null, parentMessageId: string | null) => void;
    onLoadChildren: (parentId: string, children: Message[]) => void;
    onReactionChildrenChange?: (targetMessageId: string, updatedChildren: UIMessage[]) => void;
    roomTokens?: RoomToken[];
}

function RoomMessageItemInner({
    message,
    room,
    activeThreads,
    toggleActiveThread,
    highlightedId,
    highlightedReaction,
    currentMessage,
    messageRefs,
    getNodeRef,
    getMessageItemState,
    onMessageClick,
    progressForFile,
    onResendMessage,
    mentionTarget,
    setMentionTarget,
    forceUpdate,
    onTargetMessage,
    onLoadChildren,
    onReactionChildrenChange,
    roomTokens,
}: RoomMessageItemProps) {
    const [activeReplyId, setActiveReplyId] = useState<string | null>(null);
    const [loadingReplies, setLoadingReplies] = useState(false);
    const [showReactionPopover, setShowReactionPopover] = useState(false); // 🔧 리액션 팝오버 상태
    const clearedNotifsRef = useRef<Set<string>>(new Set()); // 🔧 이미 클리어한 알림 추적

    const isThreadExpanded = activeThreads.has(message.msg.id);




    const handleReplyToClick = (targetId: string, currentId: string) => {
        // 1. Check if target is the main message itself
        if (targetId === message.msg.id) {
            const mainRef = getNodeRef(message.msg.id);
            if (mainRef.current) {
                mainRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                const element = mainRef.current.querySelector('.message-box');
                if (element) {
                    element.classList.add('bg-primary/20', 'ring-2', 'ring-primary/50');
                    setTimeout(() => element.classList.remove('bg-primary/20', 'ring-2', 'ring-primary/50'), 2000);
                }
            }
            return;
        }

        // 2. Check if target is in the current thread replies
        const targetReply = replies.find(r => r.msg.id === targetId);
        if (targetReply) {
            const targetRef = getNodeRef(targetId);
            if (targetRef.current) {
                targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });

                // Add highlight effect to the inner message container if possible, or the wrapper
                // The wrapper has the ref, so we add classes there or find the inner wrapper
                targetRef.current.classList.add('bg-primary/20', 'ring-2', 'ring-primary/50');
                setTimeout(() => targetRef.current?.classList.remove('bg-primary/20', 'ring-2', 'ring-primary/50'), 2000);
            }
            return;
        }

        // 3. Not found locally, delegate to parent logic
        onTargetMessage(targetId, message.msg.id);
    };

    const nodeRef = getNodeRef(message.msg.id);
    const replies = message.nonReactions();
    // 리액션을 제외한 답글 수 사용
    // children이 로드되지 않은 경우(length === 0)에만 message.replyCount 사용
    const replyCount = replies.length > 0 ? replies.length : (message.replyCount ?? 0);

    // lastReply derived from either loaded children or summary data
    const lastReplyMsg = message.lastReply || (replies.length > 0 ? replies[replies.length - 1].msg : null);

    // 🔧 MyNotifs에서 해당 스레드의 읽지 않은 알림 개수 계산
    // (스레드가 로드되지 않은 상태에서도 알림 기반으로 표시)
    const unreadFromNotifs = (() => {
        const roomNotifs = MyNotifs.NotificationsByRoom.get(room.id) ?? [];
        return roomNotifs.filter(n =>
            n.isNew &&
            n.parentMessageId === message.msg.id &&
            n.type !== 'Emoticon'
        ).length;
    })();

    // 기존 unreadReplyCount와 알림 기반 개수 중 큰 값 사용
    const totalUnread = Math.max(message.msg.unreadReplyCount ?? 0, unreadFromNotifs);

    const isAutoMessage = !!message.msg.autoMessage;

    // Handle jump to reply from outside (e.g. notification)
    useEffect(() => {
        if (!highlightedId) return;

        // If highlightedId is one of the loaded replies, scroll into view
        const isTargetInReplies = replies.some(r => r.msg.id === highlightedId);

        if (isThreadExpanded && isTargetInReplies) {
            setTimeout(() => {
                const targetRef = getNodeRef(highlightedId);
                if (targetRef.current) {
                    targetRef.current.scrollIntoView({ behavior: 'auto', block: 'center' });
                }
            }, 100);
        }
    }, [highlightedId, isThreadExpanded, replies]);

    // Lazy load replies when expanded
    useEffect(() => {
        const isLoaded = message.repliesLoaded || message.msg.repliesLoaded;

        if (isThreadExpanded && !isLoaded && !loadingReplies) {
            setLoadingReplies(true);
            Fire.msg.getChildrenMessages(My.user.id, message.msg.id).then((msgs: Message[]) => {
                onLoadChildren(message.msg.id, msgs);
                // We set internal loading to false, but the "isLoaded" check above
                // depends on the new prop "message" arriving from RoomContent
                setLoadingReplies(false);
            }).catch((e: Error) => {
                setLoadingReplies(false);
            });
        }

        // Mark as read if expanded
        if (isThreadExpanded && message.msg.unreadReplyCount && message.msg.unreadReplyCount > 0) {
            message.msg.unreadReplyCount = 0;
            forceUpdate();
        }

        // 🔧 스레드 펼침 시 신규 댓글 알림 읽음 처리 (중복 방지)
        if (isThreadExpanded && replies.length > 0) {
            const newReplyNotifs = replies
                .filter(reply => reply.isNew())
                .map(reply => MyNotifs.NotificationsByMessage.get(reply.msg.id))
                .filter((notif): notif is NonNullable<typeof notif> =>
                    notif != null && notif.isNew && !clearedNotifsRef.current.has(notif.id)
                );

            if (newReplyNotifs.length > 0) {
                newReplyNotifs.forEach(n => clearedNotifsRef.current.add(n.id));
                Fire.clearNotifications(newReplyNotifs);
            }
        }
    }, [isThreadExpanded, message.repliesLoaded, message.msg.repliesLoaded, message.msg.unreadReplyCount, loadingReplies, replies]);

    const loadedReplyUsers = replies.reduce((acc, reply) => {
        if (reply?.msg?.user && !acc.find(u => u.id === reply.msg.user.id)) {
            acc.push(reply.msg.user);
        }
        return acc;
    }, [] as { id: string; name: string; photoUrl: string | null }[]);

    const replyUsers = loadedReplyUsers.length > 0 ? loadedReplyUsers.slice(0, 3) : (lastReplyMsg?.user ? [lastReplyMsg.user] : []);

    return (
        <div className="message-root px-4" id={`ref-${message.msg.id}`}>
            {message.isNewDay && (
                <div className="flex items-center gap-4 mb-6">
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-meta font-bold leading-[20px] text-muted-foreground whitespace-nowrap tracking-tight">
                        {UtilApp.dateStrToDay(message.msg.created, My.user.lang)}
                    </span>
                    <div className="flex-1 h-px bg-border" />
                </div>
            )}
            <div
                className={cn(
                    "flex flex-col scroll-m-4 transition-all relative rounded-panel overflow-clip",
                    isAutoMessage
                        ? "py-0 px-0"
                        : "border",
                    !isAutoMessage && (currentMessage?.msg.id === message.msg.id || replies.some(r => currentMessage?.msg.id === r.msg.id))
                        ? "bg-[rgb(var(--msg-selected-bg))] border-[color:var(--msg-selected-border)]"
                        : !isAutoMessage ? "bg-[rgb(var(--message-bubble))] border-border" : ""
                )}
                role='article'
                data-message-id={message.msg.id}
            >
                {/* ── 부모 메시지 그룹 ── */}
                <div
                    className={cn(
                        "flex flex-col transition-all",
                        isAutoMessage
                            ? ""
                            : "py-3.5 px-4 rounded-panel",
                        !isAutoMessage && currentMessage?.msg.id === message.msg.id && "bg-primary/10"
                    )}
                >
                    {/* Main Message */}
                    <CSSTransition
                        nodeRef={nodeRef}
                        in={message.msg.id === highlightedId}
                        timeout={2000}
                        classNames="flash"
                        unmountOnExit={false}
                    >
                        <div
                            ref={(el) => {
                                (nodeRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                                messageRefs.current[message.msg.id] = el;
                            }}
                            className={cn(
                                "message-box rounded-control transition-colors",
                                message.msg.id === highlightedId && "bg-primary/20"
                            )}
                        >
                            <MessageItem
                                m={message}
                                state={getMessageItemState(message)}
                                onMessageClick={onMessageClick}
                                progressForFile={progressForFile}
                                onResendMessage={onResendMessage}
                                highlightedReaction={highlightedReaction}
                                roomUsers={room.users}
                                roomTokens={roomTokens}
                            />

                            {/* 🔧 Bottom bar: 쓰레드 버튼 + 리액션 버튼 + 리액션 요약 */}
                            <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                {/* 쓰레드 버튼 */}
                                {(replyCount > 0 || isThreadExpanded) && (
                                    <button
                                        className="inline-flex items-center justify-center gap-1 h-7 min-w-11 px-2 bg-background border border-border rounded-md text-body text-muted-foreground hover:bg-accent hover:border-border transition-colors shadow-sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            toggleActiveThread(message.msg.id);
                                        }}
                                    >
                                        <span>{t`Replies`} {replyCount > 0 ? replyCount : ''}</span>
                                        {totalUnread > 0 && (
                                            <Badge className="h-3.5 min-w-[0.875rem] px-0.5 flex items-center justify-center text-meta-sm bg-primary text-on-primary-text rounded-full border-none">
                                                {totalUnread}
                                            </Badge>
                                        )}
                                        {isThreadExpanded ? (
                                            <ChevronUp className="w-3 h-3 text-muted-foreground" />
                                        ) : (
                                            <IconChevronDown className="w-3 h-3 text-muted-foreground" />
                                        )}
                                    </button>
                                )}

                                {/* 리액션 버튼 */}
                                {!isAutoMessage && (
                                    <Popover open={showReactionPopover} onOpenChange={setShowReactionPopover} modal>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="icon-reaction"
                                                data-state={showReactionPopover ? "on" : undefined}
                                            >
                                                <IconSmilePlus />
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent
                                            className="p-3 rounded-xl w-auto"
                                            side="right"
                                            align="start"
                                            sideOffset={4}
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <MsgInputFooterReactions room={room} parentMessage={message} onReactionChildrenChange={onReactionChildrenChange} onReactionUpdate={() => { forceUpdate(); setShowReactionPopover(false); }} />
                                        </PopoverContent>
                                    </Popover>
                                )}

                                {/* 리액션 요약 */}
                                <GetReactionSummary emotes={message.reactions()} highlightedEmoticon={highlightedReaction?.messageId === message.msg.id ? highlightedReaction.emoticon : null} roomUsers={room.users} />
                            </div>
                        </div>
                    </CSSTransition>
                </div>

                {/* ── 자식 메시지 그룹 (Expanded Thread) ── */}
                {(replyCount > 0 || isThreadExpanded) && isThreadExpanded && (
                    <div className="pt-3 pl-8 pr-4 pb-2 w-full">
                        {loadingReplies && (
                            <div className="flex items-center gap-2 py-2 text-muted-foreground italic text-xs">
                                <Loader2 className="w-3 h-3 animate-spin" />
                                <span>{t`Loading replies...`}</span>
                            </div>
                        )}
                        {/* Replies */}
                        {replies.map((reply) => {
                            const replyNodeRef = getNodeRef(reply.msg.id);
                            const isHighlighted = highlightedId === reply.msg.id;
                            const isReplyReactionOpen = activeReplyId === reply.msg.id;
                            const isSelected = currentMessage?.msg.id === reply.msg.id;

                            return (
                                <div key={reply.msg.id} className="mb-3">
                                    <div
                                        className={cn(
                                            "relative rounded-xl transition-colors p-2",
                                            isSelected && "bg-primary/10",
                                            isHighlighted && "bg-primary/15"
                                        )}
                                        ref={replyNodeRef}
                                    >
                                        {/* ← 답글 화살표 (↩) - Figma */}
                                        <div className="flex gap-2">
                                            <CornerDownRight className="w-4 h-4 text-muted-foreground shrink-0 mt-3" />
                                            <div className="flex-1 min-w-0">
                                                <MessageItem
                                                    m={reply}
                                                    state={getMessageItemState(reply)}
                                                    onMessageClick={onMessageClick}
                                                    progressForFile={progressForFile}
                                                    onResendMessage={onResendMessage}
                                                    onReplyToClick={(targetId) => handleReplyToClick(targetId, reply.msg.id)}
                                                    roomUsers={room.users}
                                                    roomTokens={roomTokens}
                                                />
                                                {/* 답글 리액션 행 */}
                                                {!reply.msg.autoMessage && (
                                                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                                    <Popover
                                                        open={isReplyReactionOpen}
                                                        onOpenChange={(open) => setActiveReplyId(open ? reply.msg.id : null)}
                                                    >
                                                        <PopoverTrigger asChild>
                                                            <Button
                                                                variant="icon-reaction"
                                                                data-state={isReplyReactionOpen ? "on" : undefined}
                                                            >
                                                                <IconSmilePlus />
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent
                                                            className="p-3 rounded-xl w-auto"
                                                            side="right"
                                                            align="start"
                                                            sideOffset={4}
                                                            onClick={(e) => e.stopPropagation()}
                                                        >
                                                            <MsgInputFooterReactions room={room} parentMessage={reply} onReactionChildrenChange={onReactionChildrenChange} onReactionUpdate={() => { forceUpdate(); setActiveReplyId(null); }} />
                                                        </PopoverContent>
                                                    </Popover>
                                                    <GetReactionSummary emotes={reply.reactions()} roomUsers={room.users} />
                                                </div>
                                                )}
                                            </div> {/* flex-1 min-w-0 end */}
                                        </div> {/* flex gap-2 (reply arrow container) end */}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

            </div>

            {/* Legacy Children */}
            {
                replyCount === 0 && message.children.length > 0 && message.nonReactions().map((c) => {
                    const childNodeRef = getNodeRef(c.msg.id);
                    return (
                        <CSSTransition
                            key={`child-${c.msg.id}`}
                            nodeRef={childNodeRef}
                            in={c.msg.id === highlightedId}
                            timeout={2000}
                            classNames="flash"
                            unmountOnExit={false}
                        >
                            <div
                                ref={(el) => {
                                    (childNodeRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
                                    messageRefs.current[c.msg.id] = el;
                                }}
                                className={cn(
                                    "message-box bg-[rgb(var(--message-bubble))] rounded-control transition-colors mt-2",
                                    currentMessage === c && "pl-2 bg-primary/20"
                                )}
                            >
                                <MessageItem m={c} state={getMessageItemState(c)} onMessageClick={onMessageClick} parent={message} progressForFile={progressForFile} onResendMessage={onResendMessage} roomUsers={room.users} roomTokens={roomTokens} />
                            </div>
                        </CSSTransition>
                    );
                })
            }
        </div >
    );
}

// React.memo with custom comparison to prevent unnecessary re-renders during scroll
export const RoomMessageItem = memo(RoomMessageItemInner, (prevProps, nextProps) => {
    // Return true if props are equal (skip re-render)
    // Return false if props are different (re-render needed)

    // Core message identity - always check
    if (prevProps.message.msg.id !== nextProps.message.msg.id) return false;

    // 🔧 isLocal 상태 변경 체크 (스피너 표시/숨김)
    if (prevProps.message.isLocal !== nextProps.message.isLocal) return false;

    // Message content changes
    if (prevProps.message.msg.text !== nextProps.message.msg.text) return false;
    if (prevProps.message.children.length !== nextProps.message.children.length) return false;
    if (prevProps.message.msg.unreadReplyCount !== nextProps.message.msg.unreadReplyCount) return false;
    if (prevProps.message.repliesLoaded !== nextProps.message.repliesLoaded) return false;

    // 🔧 webhookDelivery 상태 변경 감지 (부모 메시지)
    const prevDelivery = JSON.stringify((prevProps.message.msg as any).webhookDelivery || null);
    const nextDelivery = JSON.stringify((nextProps.message.msg as any).webhookDelivery || null);
    if (prevDelivery !== nextDelivery) return false;

    // 🔧 자식 메시지의 isLocal 상태 변경, grandKids(리액션) 수 변화, 그리고 webhookDelivery 변경 체크
    for (let i = 0; i < prevProps.message.children.length; i++) {
        if (prevProps.message.children[i]?.isLocal !== nextProps.message.children[i]?.isLocal) {
            return false;
        }
        // grandKids(리액션) 수가 변하면 리렌더링 필요
        if (prevProps.message.children[i]?.children?.length !== nextProps.message.children[i]?.children?.length) {
            return false;
        }
        // 🔧 grandKids(리액션)의 isLocal 상태 변경 감지 — 댓글에 리액션 시 흐림 해제용
        const prevGrandKids = prevProps.message.children[i]?.children ?? [];
        const nextGrandKids = nextProps.message.children[i]?.children ?? [];
        for (let j = 0; j < Math.max(prevGrandKids.length, nextGrandKids.length); j++) {
            if (prevGrandKids[j]?.isLocal !== nextGrandKids[j]?.isLocal) {
                return false;
            }
        }
        // 자식 메시지의 webhookDelivery 상태 변경 감지
        const pcd = JSON.stringify((prevProps.message.children[i]?.msg as any).webhookDelivery || null);
        const ncd = JSON.stringify((nextProps.message.children[i]?.msg as any).webhookDelivery || null);
        if (pcd !== ncd) {
            return false;
        }
    }

    // Highlight state - important for visual feedback
    const wasHighlighted = prevProps.highlightedId === prevProps.message.msg.id;
    const isHighlighted = nextProps.highlightedId === nextProps.message.msg.id;
    if (wasHighlighted !== isHighlighted) return false;

    // Check if any child is highlighted
    const prevChildHighlighted = prevProps.message.children.some(c => c.msg.id === prevProps.highlightedId);
    const nextChildHighlighted = nextProps.message.children.some(c => c.msg.id === nextProps.highlightedId);
    if (prevChildHighlighted !== nextChildHighlighted) return false;

    // Thread expansion state
    const wasExpanded = prevProps.activeThreads.has(prevProps.message.msg.id);
    const isExpanded = nextProps.activeThreads.has(nextProps.message.msg.id);
    if (wasExpanded !== isExpanded) return false;

    // Current message selection (부모 메시지)
    const wasSelected = prevProps.currentMessage?.msg.id === prevProps.message.msg.id;
    const isSelected = nextProps.currentMessage?.msg.id === nextProps.message.msg.id;
    if (wasSelected !== isSelected) {
        return false;
    }

    // 🔧 스레드 내 답글의 선택 상태 변경 감지 (어떤 자식이 선택되었는지 ID로 비교)
    const prevSelectedChildId = prevProps.message.children.find(c => c.msg.id === prevProps.currentMessage?.msg.id)?.msg.id;
    const nextSelectedChildId = nextProps.message.children.find(c => c.msg.id === nextProps.currentMessage?.msg.id)?.msg.id;
    if (prevSelectedChildId !== nextSelectedChildId) {
        return false;
    }

    // Highlighted reaction
    if (prevProps.highlightedReaction?.messageId !== nextProps.highlightedReaction?.messageId) return false;
    if (prevProps.highlightedReaction?.emoticon !== nextProps.highlightedReaction?.emoticon) return false;

    // Mention target (only check if this message is involved)
    const prevMention = prevProps.mentionTarget?.threadId === prevProps.message.msg.id;
    const nextMention = nextProps.mentionTarget?.threadId === nextProps.message.msg.id;
    if (prevMention !== nextMention) return false;

    // Room changes (rare but important)
    if (prevProps.room.id !== nextProps.room.id) return false;

    // Token list changes (외부 API 토큰 이름 갱신)
    if (prevProps.roomTokens !== nextProps.roomTokens) return false;

    // All important props are equal, skip re-render
    return true;
});
